import React from 'react';
import { useParams, Link } from 'react-router-dom';
import './ServiceDetail.css';

const ServiceDetail = () => {
  const { serviceId } = useParams();

  const servicesData = {
    // Individual treatment pages
    'dental-implants': {
      title: "Dental Implants",
      subtitle: "Permanent Tooth Replacement Solutions",
      description: "Individual implant for single or multiple missing tooth replacement with custom crown at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid specializes in dental implant procedures, providing permanent solutions for missing teeth. Our implants are designed to look, feel, and function like natural teeth.",
        benefits: [
          "Permanent tooth replacement",
          "Natural appearance and function",
          "Preserves jawbone structure",
          "No impact on adjacent teeth",
          "Long-lasting solution"
        ],
        process: [
          {
            step: 1,
            title: "Initial Assessment",
            description: "Comprehensive examination and bone density evaluation"
          },
          {
            step: 2,
            title: "Implant Placement",
            description: "Surgical placement of titanium implant"
          },
          {
            step: 3,
            title: "Healing Period",
            description: "Osseointegration and healing process"
          },
          {
            step: 4,
            title: "Crown Placement",
            description: "Attachment of custom crown"
          }
        ]
      },
      treatments: [
        {
          name: "Dental Implants",
          description: "Individual implant for single or multiple missing tooth replacement with custom crown.",
          duration: "3-6 months",
          price: "Starting from PKR 60,000",
          features: ["Titanium implant", "Custom crown", "Natural appearance", "Permanent solution"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "How long do dental implants last?",
          answer: "With proper care, dental implants can last a lifetime. They have a success rate of over 95%."
        },
        {
          question: "Is the implant procedure painful?",
          answer: "The procedure is performed under local anesthesia. Most patients experience minimal discomfort during recovery."
        },
        {
          question: "Am I a candidate for dental implants?",
          answer: "Most people with good oral health and adequate bone density are candidates. A consultation will determine your suitability."
        }
      ]
    },
    'implant-repairs': {
      title: "Implant Repairs",
      subtitle: "Professional Implant Maintenance",
      description: "Professional repair and maintenance of existing dental implants at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides expert implant repair services to restore function and maintain your dental implant investment.",
        benefits: [
          "Restores implant function",
          "Cost-effective solution",
          "Extends implant life",
          "Professional expertise",
          "Quick resolution"
        ],
        process: [
          {
            step: 1,
            title: "Assessment",
            description: "Thorough evaluation of implant condition"
          },
          {
            step: 2,
            title: "Diagnosis",
            description: "Identifying the specific repair needed"
          },
          {
            step: 3,
            title: "Repair Procedure",
            description: "Professional repair execution"
          },
          {
            step: 4,
            title: "Follow-up",
            description: "Monitoring and maintenance guidance"
          }
        ]
      },
      treatments: [
        {
          name: "Implant Repairs",
          description: "Professional repair and maintenance of existing dental implants.",
          duration: "1-2 visits",
          price: "Starting from PKR 15,000",
          features: ["Expert repair", "Maintenance service", "Restored function", "Long-term care"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "What are common implant problems?",
          answer: "Common issues include loose crowns, damaged abutments, or peri-implantitis. Most can be successfully repaired."
        },
        {
          question: "How do I know if my implant needs repair?",
          answer: "Signs include looseness, pain, swelling, or changes in bite. Regular check-ups help detect issues early."
        },
        {
          question: "Can all implant problems be repaired?",
          answer: "Most implant issues can be successfully repaired. In rare cases, implant replacement may be necessary."
        }
      ]
    },
    'bone-graft-sinus-lift': {
      title: "Bone Graft & Sinus Lift",
      subtitle: "Advanced Bone Augmentation",
      description: "Artificial bone grafting and maxillary sinus lift procedures to support implant placement at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides advanced bone grafting and sinus lift procedures to create adequate bone volume for successful implant placement.",
        benefits: [
          "Enables implant placement",
          "Restores bone volume",
          "Advanced techniques",
          "High success rate",
          "Long-term stability"
        ],
        process: [
          {
            step: 1,
            title: "Evaluation",
            description: "3D imaging and bone assessment"
          },
          {
            step: 2,
            title: "Procedure Planning",
            description: "Customized surgical approach"
          },
          {
            step: 3,
            title: "Grafting/Lift Surgery",
            description: "Bone graft or sinus lift procedure"
          },
          {
            step: 4,
            title: "Healing & Integration",
            description: "Bone healing and integration period"
          }
        ]
      },
      treatments: [
        {
          name: "Artificial Bone Graft (Augmentation)",
          description: "Bone grafting procedures to support implant placement.",
          duration: "2-4 visits",
          price: "Starting from PKR 25,000",
          features: ["Bone augmentation", "Implant support", "Advanced technique", "Healing support"],
          image: "/api/placeholder/300/200"
        },
        {
          name: "Maxillary Sinus Lift and Sinus Treatment",
          description: "Sinus lift procedures to create space for upper jaw implants.",
          duration: "2-3 visits",
          price: "Starting from PKR 30,000",
          features: ["Sinus elevation", "Implant preparation", "Specialized procedure", "Expert care"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "When is bone grafting necessary?",
          answer: "Bone grafting is needed when there's insufficient bone volume to support dental implants."
        },
        {
          question: "How long does bone graft healing take?",
          answer: "Healing typically takes 3-6 months before implants can be placed."
        },
        {
          question: "Is sinus lift surgery painful?",
          answer: "The procedure is performed under anesthesia. Post-operative discomfort is manageable with medication."
        }
      ]
    },
    'orthodontics': {
      title: "Orthodontics",
      subtitle: "Straighten Your Teeth with Expert Care",
      description: "Professional orthodontic treatments to correct misaligned teeth and improve your bite for a perfect smile at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid specializes in orthodontic treatments to correct misaligned teeth and bite problems. We offer modern orthodontic solutions to help you achieve a perfectly aligned smile.",
        benefits: [
          "Improved oral health and hygiene",
          "Enhanced facial aesthetics",
          "Better bite function",
          "Increased self-confidence",
          "Prevention of dental problems"
        ],
        process: [
          {
            step: 1,
            title: "Initial Consultation",
            description: "Comprehensive examination and orthodontic assessment"
          },
          {
            step: 2,
            title: "Treatment Plan",
            description: "Customized orthodontic treatment approach"
          },
          {
            step: 3,
            title: "Active Treatment",
            description: "Regular adjustments and progress monitoring"
          },
          {
            step: 4,
            title: "Retention Phase",
            description: "Maintaining results with retainers"
          }
        ]
      },
      treatments: [
        {
          name: "Traditional Braces",
          description: "Effective metal braces for comprehensive teeth straightening with precise control over tooth movement.",
          duration: "18-24 months",
          price: "Starting from PKR 80,000",
          features: ["Most effective for complex cases", "Durable and reliable", "Cost-effective option", "Suitable for all ages"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "How long does orthodontic treatment take?",
          answer: "Treatment duration varies depending on the complexity of your case, but typically ranges from 18-24 months."
        },
        {
          question: "Is orthodontic treatment painful?",
          answer: "You may experience mild discomfort after adjustments, but this is normal and typically subsides within a few days."
        },
        {
          question: "How often do I need to visit during treatment?",
          answer: "Regular check-ups are typically scheduled every 4-6 weeks to monitor progress and make adjustments."
        }
      ]
    },
    'implants': {
      title: "Implants",
      subtitle: "Permanent Tooth Replacement Solutions",
      description: "Advanced implant dentistry including placement, repairs, and supporting procedures for lasting results at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid specializes in dental implant procedures, providing permanent solutions for missing teeth. Our implants are designed to look, feel, and function like natural teeth.",
        benefits: [
          "Permanent tooth replacement",
          "Natural appearance and function",
          "Preserves jawbone structure",
          "No impact on adjacent teeth",
          "Long-lasting solution"
        ],
        process: [
          {
            step: 1,
            title: "Initial Assessment",
            description: "Comprehensive examination and bone density evaluation"
          },
          {
            step: 2,
            title: "Implant Placement",
            description: "Surgical placement of titanium implant"
          },
          {
            step: 3,
            title: "Healing Period",
            description: "Osseointegration and healing process"
          },
          {
            step: 4,
            title: "Crown Placement",
            description: "Attachment of custom crown"
          }
        ]
      },
      treatments: [
        {
          name: "Dental Implants",
          description: "Individual implant for single or multiple missing tooth replacement with custom crown.",
          duration: "3-6 months",
          price: "Starting from PKR 60,000",
          features: ["Titanium implant", "Custom crown", "Natural appearance", "Permanent solution"],
          image: "/api/placeholder/300/200"
        },
        {
          name: "Implant Repairs",
          description: "Professional repair and maintenance of existing dental implants.",
          duration: "1-2 visits",
          price: "Starting from PKR 15,000",
          features: ["Expert repair", "Maintenance service", "Restored function", "Long-term care"],
          image: "/api/placeholder/300/200"
        },
        {
          name: "Artificial Bone Graft (Augmentation)",
          description: "Bone grafting procedures to support implant placement.",
          duration: "2-4 visits",
          price: "Starting from PKR 25,000",
          features: ["Bone augmentation", "Implant support", "Advanced technique", "Healing support"],
          image: "/api/placeholder/300/200"
        },
        {
          name: "Maxillary Sinus Lift and Sinus Treatment",
          description: "Sinus lift procedures to create space for upper jaw implants.",
          duration: "2-3 visits",
          price: "Starting from PKR 30,000",
          features: ["Sinus elevation", "Implant preparation", "Specialized procedure", "Expert care"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "How long do dental implants last?",
          answer: "With proper care, dental implants can last a lifetime. They have a success rate of over 95%."
        },
        {
          question: "Is the implant procedure painful?",
          answer: "The procedure is performed under local anesthesia. Most patients experience minimal discomfort during recovery."
        },
        {
          question: "Can implants be repaired if damaged?",
          answer: "Yes, we offer professional implant repair services to restore function and maintain your investment."
        }
      ]
    },
    'cosmetic': {
      title: "Cosmetic Dentistry",
      subtitle: "Enhance Your Natural Beauty",
      description: "Comprehensive cosmetic treatments to improve the appearance and function of your teeth at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides comprehensive cosmetic dentistry services to enhance your smile's appearance while maintaining function and health.",
        benefits: [
          "Enhanced smile aesthetics",
          "Improved self-confidence",
          "Natural-looking results",
          "Long-lasting treatments",
          "Functional improvements"
        ],
        process: [
          {
            step: 1,
            title: "Smile Assessment",
            description: "Comprehensive evaluation of your smile and aesthetic goals"
          },
          {
            step: 2,
            title: "Treatment Planning",
            description: "Customized cosmetic treatment plan"
          },
          {
            step: 3,
            title: "Treatment Execution",
            description: "Professional cosmetic procedures"
          },
          {
            step: 4,
            title: "Follow-up Care",
            description: "Maintenance and long-term care"
          }
        ]
      },
      treatments: [
        {
          name: "Cosmetic Treatments",
          description: "Complete smile makeovers and aesthetic improvements for a beautiful smile.",
          duration: "2-4 visits",
          price: "Starting from PKR 20,000",
          features: ["Smile makeover", "Aesthetic enhancement", "Professional results", "Customized approach"],
          image: "/api/placeholder/300/200"
        },
        {
          name: "Composite Restoration",
          description: "Tooth-colored fillings and restorations that blend naturally with your teeth.",
          duration: "1-2 visits",
          price: "Starting from PKR 3,000",
          features: ["Natural appearance", "Mercury-free", "Same-day treatment", "Durable materials"],
          image: "/api/placeholder/300/200"
        },
        {
          name: "PFM and Zirconia Crowns",
          description: "High-quality porcelain-fused-metal and zirconia crowns for strength and beauty.",
          duration: "2-3 visits",
          price: "Starting from PKR 12,000",
          features: ["Superior strength", "Natural appearance", "Long-lasting", "Biocompatible"],
          image: "/api/placeholder/300/200"
        },
        {
          name: "Partial and Complete Dentures",
          description: "Custom-made dentures for missing teeth replacement with natural appearance.",
          duration: "4-6 weeks",
          price: "Starting from PKR 25,000",
          features: ["Custom-fitted", "Natural appearance", "Restored function", "Comfortable wear"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "How long do cosmetic treatments last?",
          answer: "The longevity varies by treatment type, but most cosmetic procedures last 5-15 years with proper care."
        },
        {
          question: "Are cosmetic treatments covered by insurance?",
          answer: "Coverage varies by insurance plan. We recommend checking with your provider for specific coverage details."
        },
        {
          question: "What is the difference between PFM and Zirconia crowns?",
          answer: "Zirconia crowns offer superior strength and aesthetics, while PFM crowns are more cost-effective with good durability."
        }
      ]
    },
    'restorative': {
      title: "Restorative Dentistry",
      subtitle: "Restore Function and Health",
      description: "Advanced restorative treatments to save and repair damaged teeth and restore oral health at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides comprehensive restorative dentistry services to save damaged teeth and restore optimal oral function and health.",
        benefits: [
          "Saves natural teeth",
          "Restores full function",
          "Eliminates pain and infection",
          "Prevents further damage",
          "Cost-effective solutions"
        ],
        process: [
          {
            step: 1,
            title: "Diagnosis",
            description: "Thorough examination and diagnostic imaging"
          },
          {
            step: 2,
            title: "Treatment Planning",
            description: "Customized restorative treatment plan"
          },
          {
            step: 3,
            title: "Restorative Treatment",
            description: "Professional restorative procedures"
          },
          {
            step: 4,
            title: "Follow-up Care",
            description: "Monitoring and maintenance"
          }
        ]
      },
      treatments: [
        {
          name: "Root Canal Treatment (RCT)",
          description: "Advanced endodontic treatment to save infected teeth and eliminate pain.",
          duration: "2-3 visits",
          price: "Starting from PKR 12,000",
          features: ["Saves natural tooth", "Eliminates infection", "Pain relief", "High success rate"],
          image: "/api/placeholder/300/200"
        },
        {
          name: "Simple and Surgical Extractions",
          description: "Professional tooth extractions with minimal discomfort and optimal healing.",
          duration: "1 visit",
          price: "Starting from PKR 3,000",
          features: ["Minimal discomfort", "Expert technique", "Proper healing", "Post-care support"],
          image: "/api/placeholder/300/200"
        },
        {
          name: "Impactions and Apicectomies",
          description: "Surgical removal of impacted teeth and root-end surgeries for complex cases.",
          duration: "1-2 visits",
          price: "Starting from PKR 8,000",
          features: ["Surgical expertise", "Complex case management", "Minimal complications", "Expert care"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "Is root canal treatment painful?",
          answer: "Modern root canal treatment is comfortable with local anesthesia. Most patients experience significant pain relief after treatment."
        },
        {
          question: "When is tooth extraction necessary?",
          answer: "Extraction is recommended when a tooth cannot be saved through other treatments or poses a risk to oral health."
        },
        {
          question: "What is an apicectomy?",
          answer: "An apicectomy is a surgical procedure to remove the tip of a tooth's root when conventional root canal treatment isn't sufficient."
        }
      ]
    },
    'periodontics': {
      title: "Periodontics (Gum Treatment)",
      subtitle: "Specialized Gum Care",
      description: "Professional periodontal therapy to treat gum disease and maintain healthy gums at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides specialized periodontal care to treat gum disease and maintain optimal gum health for overall oral wellness.",
        benefits: [
          "Treats gum disease",
          "Prevents tooth loss",
          "Eliminates infection",
          "Improves oral health",
          "Fresh breath"
        ],
        process: [
          {
            step: 1,
            title: "Periodontal Assessment",
            description: "Comprehensive gum health evaluation"
          },
          {
            step: 2,
            title: "Treatment Planning",
            description: "Customized periodontal therapy plan"
          },
          {
            step: 3,
            title: "Active Treatment",
            description: "Scaling and root planing procedures"
          },
          {
            step: 4,
            title: "Maintenance",
            description: "Regular periodontal maintenance"
          }
        ]
      },
      treatments: [
        {
          name: "Scaling and Root Planning (SRP)",
          description: "Deep cleaning procedure to treat gum disease and remove bacterial infection.",
          duration: "2-4 visits",
          price: "Starting from PKR 5,000",
          features: ["Deep cleaning", "Infection removal", "Gum healing", "Disease prevention"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "What is scaling and root planing?",
          answer: "SRP is a deep cleaning procedure that removes plaque and tartar from below the gum line and smooths root surfaces."
        },
        {
          question: "How often should I have periodontal treatment?",
          answer: "Treatment frequency depends on the severity of gum disease, typically every 3-4 months for maintenance."
        },
        {
          question: "Is periodontal treatment painful?",
          answer: "Local anesthesia is used to ensure comfort during treatment. Some sensitivity may occur afterward but is temporary."
        }
      ]
    },
    'surgery': {
      title: "Oral and Maxillofacial Surgery",
      subtitle: "Advanced Surgical Procedures",
      description: "Specialized surgical treatments for complex oral and facial conditions at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides comprehensive oral and maxillofacial surgery services for complex conditions requiring surgical intervention.",
        benefits: [
          "Expert surgical care",
          "Advanced techniques",
          "Minimal complications",
          "Faster healing",
          "Comprehensive treatment"
        ],
        process: [
          {
            step: 1,
            title: "Surgical Consultation",
            description: "Detailed examination and surgical planning"
          },
          {
            step: 2,
            title: "Pre-operative Preparation",
            description: "Patient preparation and anesthesia planning"
          },
          {
            step: 3,
            title: "Surgical Procedure",
            description: "Expert surgical intervention"
          },
          {
            step: 4,
            title: "Post-operative Care",
            description: "Recovery monitoring and follow-up"
          }
        ]
      },
      treatments: [
        {
          name: "Soft Tissue Surgeries",
          description: "Surgical procedures involving oral soft tissues for various conditions.",
          duration: "1-2 visits",
          price: "Starting from PKR 8,000",
          features: ["Precise surgery", "Minimal scarring", "Expert technique", "Optimal healing"],
          image: "/api/placeholder/300/200"
        },
        {
          name: "Tongue Tie Treatment",
          description: "Surgical correction of tongue tie (ankyloglossia) for improved function.",
          duration: "1 visit",
          price: "Starting from PKR 5,000",
          features: ["Improved function", "Quick procedure", "Minimal discomfort", "Better speech"],
          image: "/api/placeholder/300/200"
        },
        {
          name: "Biopsies",
          description: "Tissue sampling for diagnostic purposes to identify various conditions.",
          duration: "1 visit",
          price: "Starting from PKR 6,000",
          features: ["Accurate diagnosis", "Minimal invasive", "Expert analysis", "Peace of mind"],
          image: "/api/placeholder/300/200"
        },
        {
          name: "Mole and Wart Removal",
          description: "Safe removal of oral moles and warts for health and aesthetic reasons.",
          duration: "1 visit",
          price: "Starting from PKR 4,000",
          features: ["Safe removal", "Minimal scarring", "Quick healing", "Aesthetic improvement"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "What conditions require oral surgery?",
          answer: "Conditions like impacted teeth, oral lesions, tongue tie, and certain infections may require surgical treatment."
        },
        {
          question: "How long is recovery after oral surgery?",
          answer: "Recovery time varies by procedure, typically ranging from a few days to several weeks."
        },
        {
          question: "Is oral surgery performed under anesthesia?",
          answer: "Yes, appropriate anesthesia or sedation is used to ensure patient comfort during surgical procedures."
        }
      ]
    },
    'tmj': {
      title: "TMJ and Jaw Disorders",
      subtitle: "Jaw Pain Relief",
      description: "Specialized treatment for temporomandibular joint disorders and jaw-related problems at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides comprehensive treatment for TMJ disorders and jaw-related problems to relieve pain and restore function.",
        benefits: [
          "Pain relief",
          "Improved jaw function",
          "Better quality of life",
          "Non-invasive options",
          "Long-term solutions"
        ],
        process: [
          {
            step: 1,
            title: "TMJ Assessment",
            description: "Comprehensive evaluation of jaw function and symptoms"
          },
          {
            step: 2,
            title: "Diagnosis",
            description: "Identifying the specific TMJ disorder"
          },
          {
            step: 3,
            title: "Treatment Plan",
            description: "Customized treatment approach"
          },
          {
            step: 4,
            title: "Ongoing Management",
            description: "Long-term care and monitoring"
          }
        ]
      },
      treatments: [
        {
          name: "TMJ Disorders Treatment",
          description: "Comprehensive treatment for jaw joint disorders, pain, and dysfunction.",
          duration: "Multiple visits",
          price: "Starting from PKR 10,000",
          features: ["Pain relief", "Function restoration", "Non-surgical options", "Long-term management"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "What are the symptoms of TMJ disorders?",
          answer: "Common symptoms include jaw pain, clicking sounds, difficulty opening the mouth, and headaches."
        },
        {
          question: "How is TMJ disorder diagnosed?",
          answer: "Diagnosis involves clinical examination, medical history, and sometimes imaging studies to assess jaw function."
        },
        {
          question: "Are TMJ treatments effective?",
          answer: "Yes, most TMJ disorders respond well to conservative treatments, with significant improvement in symptoms."
        }
      ]
    },
    // Additional individual treatment pages
    'cosmetic-dentistry': {
      title: "Cosmetic Treatments",
      subtitle: "Complete Smile Makeovers",
      description: "Complete smile makeovers and aesthetic improvements for a beautiful smile at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides comprehensive cosmetic dentistry services to enhance your smile's appearance while maintaining function and health.",
        benefits: [
          "Enhanced smile aesthetics",
          "Improved self-confidence",
          "Natural-looking results",
          "Long-lasting treatments",
          "Functional improvements"
        ],
        process: [
          {
            step: 1,
            title: "Smile Assessment",
            description: "Comprehensive evaluation of your smile and aesthetic goals"
          },
          {
            step: 2,
            title: "Treatment Planning",
            description: "Customized cosmetic treatment plan"
          },
          {
            step: 3,
            title: "Treatment Execution",
            description: "Professional cosmetic procedures"
          },
          {
            step: 4,
            title: "Follow-up Care",
            description: "Maintenance and long-term care"
          }
        ]
      },
      treatments: [
        {
          name: "Cosmetic Treatments",
          description: "Complete smile makeovers and aesthetic improvements for a beautiful smile.",
          duration: "2-4 visits",
          price: "Starting from PKR 20,000",
          features: ["Smile makeover", "Aesthetic enhancement", "Professional results", "Customized approach"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "What does a smile makeover include?",
          answer: "A smile makeover can include teeth whitening, veneers, crowns, and other cosmetic treatments tailored to your needs."
        },
        {
          question: "How long do cosmetic treatments last?",
          answer: "The longevity varies by treatment type, but most cosmetic procedures last 5-15 years with proper care."
        },
        {
          question: "Are cosmetic treatments painful?",
          answer: "Most cosmetic procedures are minimally invasive with little to no discomfort during or after treatment."
        }
      ]
    },
    'composite-restoration': {
      title: "Composite Restoration",
      subtitle: "Tooth-Colored Fillings",
      description: "Tooth-colored fillings and restorations that blend naturally with your teeth at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid uses advanced composite materials to provide natural-looking restorations that preserve tooth structure and aesthetics.",
        benefits: [
          "Natural appearance",
          "Mercury-free material",
          "Conservative treatment",
          "Strong bonding",
          "Same-day completion"
        ],
        process: [
          {
            step: 1,
            title: "Examination",
            description: "Assessment of decay or damage"
          },
          {
            step: 2,
            title: "Preparation",
            description: "Removal of decay and tooth preparation"
          },
          {
            step: 3,
            title: "Restoration",
            description: "Composite placement and shaping"
          },
          {
            step: 4,
            title: "Finishing",
            description: "Polishing and bite adjustment"
          }
        ]
      },
      treatments: [
        {
          name: "Composite Restoration",
          description: "Tooth-colored fillings and restorations that blend naturally with your teeth.",
          duration: "1-2 visits",
          price: "Starting from PKR 3,000",
          features: ["Natural appearance", "Mercury-free", "Same-day treatment", "Durable materials"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "How long do composite fillings last?",
          answer: "Composite fillings typically last 5-10 years with proper care and oral hygiene."
        },
        {
          question: "Are composite fillings safe?",
          answer: "Yes, composite fillings are mercury-free and biocompatible, making them a safe choice."
        },
        {
          question: "Can composite fillings replace amalgam fillings?",
          answer: "Yes, old amalgam fillings can be safely replaced with tooth-colored composite restorations."
        }
      ]
    },
    'crowns': {
      title: "PFM & Zirconia Crowns",
      subtitle: "Premium Crown Solutions",
      description: "High-quality porcelain-fused-metal and zirconia crowns for strength and beauty at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides premium crown solutions using PFM and zirconia materials for optimal strength, durability, and aesthetics.",
        benefits: [
          "Superior strength",
          "Natural appearance",
          "Long-lasting solution",
          "Biocompatible materials",
          "Custom-made fit"
        ],
        process: [
          {
            step: 1,
            title: "Consultation",
            description: "Crown assessment and material selection"
          },
          {
            step: 2,
            title: "Preparation",
            description: "Tooth preparation and impression"
          },
          {
            step: 3,
            title: "Temporary Crown",
            description: "Temporary crown placement"
          },
          {
            step: 4,
            title: "Final Crown",
            description: "Permanent crown cementation"
          }
        ]
      },
      treatments: [
        {
          name: "PFM and Zirconia Crowns",
          description: "High-quality porcelain-fused-metal and zirconia crowns for strength and beauty.",
          duration: "2-3 visits",
          price: "Starting from PKR 12,000",
          features: ["Superior strength", "Natural appearance", "Long-lasting", "Biocompatible"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "What is the difference between PFM and Zirconia crowns?",
          answer: "Zirconia crowns offer superior strength and aesthetics, while PFM crowns are more cost-effective with good durability."
        },
        {
          question: "How long do crowns last?",
          answer: "With proper care, crowns can last 10-15 years or longer."
        },
        {
          question: "Do crowns require special care?",
          answer: "Crowns require regular brushing, flossing, and dental check-ups, just like natural teeth."
        }
      ]
    },
    'dentures': {
      title: "Partial & Complete Dentures",
      subtitle: "Removable Tooth Replacement",
      description: "Custom-made dentures for missing teeth replacement with natural appearance at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid creates custom-fitted dentures that restore function and appearance for patients with missing teeth.",
        benefits: [
          "Restored chewing function",
          "Natural appearance",
          "Improved speech",
          "Enhanced confidence",
          "Cost-effective solution"
        ],
        process: [
          {
            step: 1,
            title: "Initial Consultation",
            description: "Assessment and treatment planning"
          },
          {
            step: 2,
            title: "Impressions",
            description: "Detailed impressions and measurements"
          },
          {
            step: 3,
            title: "Try-in",
            description: "Fitting and adjustments"
          },
          {
            step: 4,
            title: "Final Delivery",
            description: "Final denture delivery and care instructions"
          }
        ]
      },
      treatments: [
        {
          name: "Partial and Complete Dentures",
          description: "Custom-made dentures for missing teeth replacement with natural appearance.",
          duration: "4-6 weeks",
          price: "Starting from PKR 25,000",
          features: ["Custom-fitted", "Natural appearance", "Restored function", "Comfortable wear"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "How long does it take to get used to dentures?",
          answer: "Most patients adjust to dentures within 2-4 weeks with regular wear and practice."
        },
        {
          question: "How do I care for my dentures?",
          answer: "Clean dentures daily with denture cleaner, soak overnight, and maintain regular dental visits."
        },
        {
          question: "Can I eat normally with dentures?",
          answer: "Yes, with proper fitting and adjustment period, you can eat most foods comfortably."
        }
      ]
    },
    'root-canal-treatment': {
      title: "Root Canal Treatment (RCT)",
      subtitle: "Save Your Natural Teeth",
      description: "Advanced endodontic treatment to save infected teeth and eliminate pain at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid specializes in root canal therapy to save infected or damaged teeth, providing pain relief and preserving your natural smile.",
        benefits: [
          "Saves natural tooth",
          "Eliminates infection",
          "Pain relief",
          "Prevents extraction",
          "High success rate"
        ],
        process: [
          {
            step: 1,
            title: "Diagnosis",
            description: "X-rays and examination to assess infection"
          },
          {
            step: 2,
            title: "Access & Cleaning",
            description: "Root canal cleaning and disinfection"
          },
          {
            step: 3,
            title: "Filling",
            description: "Root canal filling and sealing"
          },
          {
            step: 4,
            title: "Restoration",
            description: "Crown or filling placement"
          }
        ]
      },
      treatments: [
        {
          name: "Root Canal Treatment (RCT)",
          description: "Advanced endodontic treatment to save infected teeth and eliminate pain.",
          duration: "2-3 visits",
          price: "Starting from PKR 12,000",
          features: ["Saves natural tooth", "Eliminates infection", "Pain relief", "High success rate"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "Is root canal treatment painful?",
          answer: "Modern root canal treatment is comfortable with local anesthesia. Most patients experience significant pain relief after treatment."
        },
        {
          question: "How long does a root canal take?",
          answer: "Most root canals are completed in 2-3 visits, each lasting 60-90 minutes."
        },
        {
          question: "What happens after root canal treatment?",
          answer: "After healing, a crown is usually placed to protect and strengthen the treated tooth."
        }
      ]
    },
    'extractions': {
      title: "Simple & Surgical Extractions",
      subtitle: "Professional Tooth Removal",
      description: "Professional tooth extractions with minimal discomfort and optimal healing at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid performs both simple and surgical extractions using advanced techniques to ensure patient comfort and optimal healing.",
        benefits: [
          "Minimal discomfort",
          "Expert technique",
          "Proper healing",
          "Post-care support",
          "Infection prevention"
        ],
        process: [
          {
            step: 1,
            title: "Assessment",
            description: "X-rays and examination"
          },
          {
            step: 2,
            title: "Anesthesia",
            description: "Local anesthesia administration"
          },
          {
            step: 3,
            title: "Extraction",
            description: "Careful tooth removal"
          },
          {
            step: 4,
            title: "Post-care",
            description: "Healing instructions and follow-up"
          }
        ]
      },
      treatments: [
        {
          name: "Simple and Surgical Extractions",
          description: "Professional tooth extractions with minimal discomfort and optimal healing.",
          duration: "1 visit",
          price: "Starting from PKR 3,000",
          features: ["Minimal discomfort", "Expert technique", "Proper healing", "Post-care support"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "When is tooth extraction necessary?",
          answer: "Extraction is recommended when a tooth cannot be saved through other treatments or poses a risk to oral health."
        },
        {
          question: "How long does healing take after extraction?",
          answer: "Initial healing takes 1-2 weeks, with complete healing occurring over several months."
        },
        {
          question: "What are the replacement options after extraction?",
          answer: "Options include dental implants, bridges, or dentures, depending on your specific situation."
        }
      ]
    },
    'surgical-procedures': {
      title: "Impactions & Apicectomies",
      subtitle: "Advanced Surgical Procedures",
      description: "Surgical removal of impacted teeth and root-end surgeries for complex cases at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides advanced surgical procedures including impacted tooth removal and apicectomies for complex dental conditions.",
        benefits: [
          "Surgical expertise",
          "Complex case management",
          "Minimal complications",
          "Expert care",
          "Advanced techniques"
        ],
        process: [
          {
            step: 1,
            title: "Surgical Consultation",
            description: "Detailed examination and surgical planning"
          },
          {
            step: 2,
            title: "Pre-operative Preparation",
            description: "Patient preparation and anesthesia planning"
          },
          {
            step: 3,
            title: "Surgical Procedure",
            description: "Expert surgical intervention"
          },
          {
            step: 4,
            title: "Post-operative Care",
            description: "Recovery monitoring and follow-up"
          }
        ]
      },
      treatments: [
        {
          name: "Impactions and Apicectomies",
          description: "Surgical removal of impacted teeth and root-end surgeries for complex cases.",
          duration: "1-2 visits",
          price: "Starting from PKR 8,000",
          features: ["Surgical expertise", "Complex case management", "Minimal complications", "Expert care"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "What is an apicectomy?",
          answer: "An apicectomy is a surgical procedure to remove the tip of a tooth's root when conventional root canal treatment isn't sufficient."
        },
        {
          question: "When are wisdom teeth impacted?",
          answer: "Wisdom teeth are impacted when there isn't enough space for them to emerge properly from the gums."
        },
        {
          question: "How long is recovery after surgical procedures?",
          answer: "Recovery typically takes 3-7 days for most procedures, with complete healing in 2-4 weeks."
        }
      ]
    },
    'scaling-root-planning': {
      title: "Scaling & Root Planning (SRP)",
      subtitle: "Deep Gum Cleaning",
      description: "Deep cleaning procedure to treat gum disease and remove bacterial infection at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides specialized scaling and root planing to treat gum disease and restore gum health.",
        benefits: [
          "Treats gum disease",
          "Removes infection",
          "Prevents tooth loss",
          "Improves oral health",
          "Fresh breath"
        ],
        process: [
          {
            step: 1,
            title: "Periodontal Assessment",
            description: "Comprehensive gum health evaluation"
          },
          {
            step: 2,
            title: "Treatment Planning",
            description: "Customized periodontal therapy plan"
          },
          {
            step: 3,
            title: "Scaling & Root Planing",
            description: "Deep cleaning procedures"
          },
          {
            step: 4,
            title: "Maintenance",
            description: "Regular periodontal maintenance"
          }
        ]
      },
      treatments: [
        {
          name: "Scaling and Root Planning (SRP)",
          description: "Deep cleaning procedure to treat gum disease and remove bacterial infection.",
          duration: "2-4 visits",
          price: "Starting from PKR 5,000",
          features: ["Deep cleaning", "Infection removal", "Gum healing", "Disease prevention"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "What is scaling and root planing?",
          answer: "SRP is a deep cleaning procedure that removes plaque and tartar from below the gum line and smooths root surfaces."
        },
        {
          question: "Is SRP painful?",
          answer: "Local anesthesia is used to ensure comfort during treatment. Some sensitivity may occur afterward but is temporary."
        },
        {
          question: "How often should I have SRP?",
          answer: "Treatment frequency depends on the severity of gum disease, typically every 3-4 months for maintenance."
        }
      ]
    },
    'soft-tissue-surgery': {
      title: "Soft Tissue Surgeries",
      subtitle: "Oral Soft Tissue Procedures",
      description: "Surgical procedures involving oral soft tissues for various conditions at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides comprehensive soft tissue surgical procedures for various oral conditions requiring surgical intervention.",
        benefits: [
          "Precise surgery",
          "Minimal scarring",
          "Expert technique",
          "Optimal healing",
          "Improved function"
        ],
        process: [
          {
            step: 1,
            title: "Consultation",
            description: "Detailed examination and surgical planning"
          },
          {
            step: 2,
            title: "Pre-operative Preparation",
            description: "Patient preparation and anesthesia planning"
          },
          {
            step: 3,
            title: "Surgical Procedure",
            description: "Expert soft tissue surgery"
          },
          {
            step: 4,
            title: "Post-operative Care",
            description: "Recovery monitoring and follow-up"
          }
        ]
      },
      treatments: [
        {
          name: "Soft Tissue Surgeries",
          description: "Surgical procedures involving oral soft tissues for various conditions.",
          duration: "1-2 visits",
          price: "Starting from PKR 8,000",
          features: ["Precise surgery", "Minimal scarring", "Expert technique", "Optimal healing"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "What conditions require soft tissue surgery?",
          answer: "Conditions like gum recession, excess gum tissue, or oral lesions may require soft tissue surgical treatment."
        },
        {
          question: "How long is recovery after soft tissue surgery?",
          answer: "Recovery typically takes 1-2 weeks, with complete healing occurring over several weeks."
        },
        {
          question: "Will there be scarring after surgery?",
          answer: "Modern surgical techniques minimize scarring, and most oral tissues heal with minimal visible scarring."
        }
      ]
    },
    'tongue-tie-treatment': {
      title: "Tongue Tie Treatment",
      subtitle: "Ankyloglossia Correction",
      description: "Surgical correction of tongue tie (ankyloglossia) for improved function at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides expert tongue tie treatment to improve speech, eating, and oral function.",
        benefits: [
          "Improved speech",
          "Better eating function",
          "Enhanced oral hygiene",
          "Quick procedure",
          "Minimal discomfort"
        ],
        process: [
          {
            step: 1,
            title: "Assessment",
            description: "Evaluation of tongue tie severity"
          },
          {
            step: 2,
            title: "Treatment Planning",
            description: "Surgical approach planning"
          },
          {
            step: 3,
            title: "Procedure",
            description: "Tongue tie release procedure"
          },
          {
            step: 4,
            title: "Follow-up",
            description: "Healing monitoring and exercises"
          }
        ]
      },
      treatments: [
        {
          name: "Tongue Tie Treatment",
          description: "Surgical correction of tongue tie (ankyloglossia) for improved function.",
          duration: "1 visit",
          price: "Starting from PKR 5,000",
          features: ["Improved function", "Quick procedure", "Minimal discomfort", "Better speech"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "What is tongue tie?",
          answer: "Tongue tie is a condition where the tongue's movement is restricted by a tight band of tissue (frenulum)."
        },
        {
          question: "At what age should tongue tie be treated?",
          answer: "Tongue tie can be treated at any age, but earlier treatment often provides better outcomes."
        },
        {
          question: "Is tongue tie treatment painful?",
          answer: "The procedure is quick and minimally invasive, with local anesthesia used for comfort."
        }
      ]
    },
    'biopsies': {
      title: "Biopsies",
      subtitle: "Tissue Diagnosis",
      description: "Tissue sampling for diagnostic purposes to identify various conditions at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid performs oral biopsies to accurately diagnose various oral conditions and provide peace of mind.",
        benefits: [
          "Accurate diagnosis",
          "Early detection",
          "Peace of mind",
          "Minimal invasive",
          "Expert analysis"
        ],
        process: [
          {
            step: 1,
            title: "Initial Examination",
            description: "Assessment of suspicious tissue"
          },
          {
            step: 2,
            title: "Biopsy Procedure",
            description: "Tissue sample collection"
          },
          {
            step: 3,
            title: "Laboratory Analysis",
            description: "Pathological examination"
          },
          {
            step: 4,
            title: "Results & Treatment",
            description: "Diagnosis discussion and treatment planning"
          }
        ]
      },
      treatments: [
        {
          name: "Biopsies",
          description: "Tissue sampling for diagnostic purposes to identify various conditions.",
          duration: "1 visit",
          price: "Starting from PKR 6,000",
          features: ["Accurate diagnosis", "Minimal invasive", "Expert analysis", "Peace of mind"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "When is a biopsy necessary?",
          answer: "A biopsy is recommended when there are suspicious lesions, growths, or changes in oral tissues."
        },
        {
          question: "Is a biopsy painful?",
          answer: "The procedure is performed under local anesthesia, so you should feel minimal discomfort."
        },
        {
          question: "How long does it take to get biopsy results?",
          answer: "Biopsy results typically take 7-10 days to receive from the pathology laboratory."
        }
      ]
    },
    'mole-wart-removal': {
      title: "Mole & Wart Removal",
      subtitle: "Oral Lesion Removal",
      description: "Safe removal of oral moles and warts for health and aesthetic reasons at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides safe and effective removal of oral moles and warts using advanced techniques.",
        benefits: [
          "Safe removal",
          "Minimal scarring",
          "Quick healing",
          "Aesthetic improvement",
          "Health protection"
        ],
        process: [
          {
            step: 1,
            title: "Assessment",
            description: "Evaluation of mole or wart"
          },
          {
            step: 2,
            title: "Treatment Planning",
            description: "Removal technique selection"
          },
          {
            step: 3,
            title: "Removal Procedure",
            description: "Safe lesion removal"
          },
          {
            step: 4,
            title: "Healing Care",
            description: "Post-procedure care and monitoring"
          }
        ]
      },
      treatments: [
        {
          name: "Mole and Wart Removal",
          description: "Safe removal of oral moles and warts for health and aesthetic reasons.",
          duration: "1 visit",
          price: "Starting from PKR 4,000",
          features: ["Safe removal", "Minimal scarring", "Quick healing", "Aesthetic improvement"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "Should oral moles be removed?",
          answer: "Oral moles should be evaluated by a professional. Removal may be recommended for health or aesthetic reasons."
        },
        {
          question: "Will removal leave a scar?",
          answer: "Modern removal techniques minimize scarring, and most oral tissues heal with minimal visible marks."
        },
        {
          question: "How long does healing take?",
          answer: "Healing typically takes 1-2 weeks, with complete tissue regeneration over several weeks."
        }
      ]
    },
    'tmj-treatment': {
      title: "TMJ Disorders Treatment",
      subtitle: "Jaw Pain Relief",
      description: "Comprehensive treatment for jaw joint disorders, pain, and dysfunction at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides comprehensive treatment for TMJ disorders and jaw-related problems to relieve pain and restore function.",
        benefits: [
          "Pain relief",
          "Improved jaw function",
          "Better quality of life",
          "Non-invasive options",
          "Long-term solutions"
        ],
        process: [
          {
            step: 1,
            title: "TMJ Assessment",
            description: "Comprehensive evaluation of jaw function and symptoms"
          },
          {
            step: 2,
            title: "Diagnosis",
            description: "Identifying the specific TMJ disorder"
          },
          {
            step: 3,
            title: "Treatment Plan",
            description: "Customized treatment approach"
          },
          {
            step: 4,
            title: "Ongoing Management",
            description: "Long-term care and monitoring"
          }
        ]
      },
      treatments: [
        {
          name: "TMJ Disorders Treatment",
          description: "Comprehensive treatment for jaw joint disorders, pain, and dysfunction.",
          duration: "Multiple visits",
          price: "Starting from PKR 10,000",
          features: ["Pain relief", "Function restoration", "Non-surgical options", "Long-term management"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "What are the symptoms of TMJ disorders?",
          answer: "Common symptoms include jaw pain, clicking sounds, difficulty opening the mouth, and headaches."
        },
        {
          question: "How is TMJ disorder diagnosed?",
          answer: "Diagnosis involves clinical examination, medical history, and sometimes imaging studies to assess jaw function."
        },
        {
          question: "Are TMJ treatments effective?",
          answer: "Yes, most TMJ disorders respond well to conservative treatments, with significant improvement in symptoms."
        }
      ]
    },
    'conscious-sedation': {
      title: "Conscious Sedation",
      subtitle: "Relaxed Dental Experience",
      description: "Safe sedation to help you relax during dental procedures while remaining conscious at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides conscious sedation services to ensure patient comfort and reduce anxiety during dental procedures.",
        benefits: [
          "Anxiety reduction",
          "Comfortable experience",
          "Conscious state maintained",
          "Safe administration",
          "Quick recovery"
        ],
        process: [
          {
            step: 1,
            title: "Pre-sedation Assessment",
            description: "Medical history and sedation planning"
          },
          {
            step: 2,
            title: "Sedation Administration",
            description: "Safe administration of chosen sedation"
          },
          {
            step: 3,
            title: "Procedure Monitoring",
            description: "Continuous monitoring during treatment"
          },
          {
            step: 4,
            title: "Recovery",
            description: "Post-sedation recovery and discharge"
          }
        ]
      },
      treatments: [
        {
          name: "Conscious Sedation",
          description: "Safe sedation to help you relax during dental procedures while remaining conscious.",
          duration: "As needed",
          price: "Starting from PKR 8,000",
          features: ["Anxiety relief", "Conscious state", "Safe monitoring", "Quick recovery"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "Is conscious sedation safe?",
          answer: "Yes, conscious sedation is very safe when administered by trained professionals with proper monitoring."
        },
        {
          question: "Will I remember the procedure under sedation?",
          answer: "With conscious sedation, you may have little to no memory of the procedure, which helps reduce anxiety."
        },
        {
          question: "How long does sedation last?",
          answer: "The effects of sedation typically wear off within a few hours, but you'll need someone to drive you home."
        }
      ]
    },
    'anesthesiologist-services': {
      title: "Anesthesiologist Services",
      subtitle: "Professional Anesthesia Care",
      description: "Professional anesthesiologist available for complex procedures requiring deeper sedation at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid works with professional anesthesiologists to provide the highest level of sedation care for complex dental procedures.",
        benefits: [
          "Expert administration",
          "Deep sedation options",
          "Maximum safety",
          "Continuous monitoring",
          "Complex procedure support"
        ],
        process: [
          {
            step: 1,
            title: "Pre-anesthesia Consultation",
            description: "Medical evaluation and anesthesia planning"
          },
          {
            step: 2,
            title: "Anesthesia Administration",
            description: "Professional anesthesia induction"
          },
          {
            step: 3,
            title: "Procedure Monitoring",
            description: "Continuous vital signs monitoring"
          },
          {
            step: 4,
            title: "Recovery Management",
            description: "Post-anesthesia recovery care"
          }
        ]
      },
      treatments: [
        {
          name: "Anesthesiologist Services",
          description: "Professional anesthesiologist available for complex procedures requiring deeper sedation.",
          duration: "As needed",
          price: "Starting from PKR 15,000",
          features: ["Expert administration", "Deep sedation", "Continuous monitoring", "Maximum safety"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "When is an anesthesiologist needed?",
          answer: "An anesthesiologist is recommended for complex procedures, multiple treatments, or patients with medical conditions requiring specialized care."
        },
        {
          question: "What is the difference between conscious sedation and general anesthesia?",
          answer: "Conscious sedation keeps you relaxed but awake, while general anesthesia renders you completely unconscious."
        },
        {
          question: "How long is recovery after general anesthesia?",
          answer: "Recovery from general anesthesia typically takes several hours, and you'll need someone to drive you home and stay with you."
        }
      ]
    },
    'anesthesia': {
      title: "Anesthesia and Sedation Services",
      subtitle: "Comfortable Treatment Experience",
      description: "Advanced anesthesia and sedation options for comfortable dental procedures at Prime Care Dental.",
      heroImage: "/api/placeholder/800/400",
      mainContent: {
        overview: "Dr. Muhammad Junaid provides comprehensive anesthesia and sedation services to ensure patient comfort during dental procedures.",
        benefits: [
          "Anxiety reduction",
          "Pain-free procedures",
          "Comfortable experience",
          "Safe administration",
          "Professional monitoring"
        ],
        process: [
          {
            step: 1,
            title: "Pre-sedation Assessment",
            description: "Medical history and sedation planning"
          },
          {
            step: 2,
            title: "Sedation Administration",
            description: "Safe administration of chosen sedation"
          },
          {
            step: 3,
            title: "Procedure Monitoring",
            description: "Continuous monitoring during treatment"
          },
          {
            step: 4,
            title: "Recovery",
            description: "Post-sedation recovery and discharge"
          }
        ]
      },
      treatments: [
        {
          name: "Conscious Sedation",
          description: "Safe sedation to help you relax during dental procedures while remaining conscious.",
          duration: "As needed",
          price: "Starting from PKR 8,000",
          features: ["Anxiety relief", "Conscious state", "Safe monitoring", "Quick recovery"],
          image: "/api/placeholder/300/200"
        },
        {
          name: "Anesthesiologist Services",
          description: "Professional anesthesiologist available for complex procedures requiring deeper sedation.",
          duration: "As needed",
          price: "Starting from PKR 15,000",
          features: ["Expert administration", "Deep sedation", "Continuous monitoring", "Maximum safety"],
          image: "/api/placeholder/300/200"
        }
      ],
      faqs: [
        {
          question: "Is conscious sedation safe?",
          answer: "Yes, conscious sedation is very safe when administered by trained professionals with proper monitoring."
        },
        {
          question: "Will I remember the procedure under sedation?",
          answer: "With conscious sedation, you may have little to no memory of the procedure, which helps reduce anxiety."
        },
        {
          question: "How long does sedation last?",
          answer: "The effects of sedation typically wear off within a few hours, but you'll need someone to drive you home."
        }
      ]
    }
  };

  const service = servicesData[serviceId];

  if (!service) {
    return (
      <div className="service-not-found">
        <h2>Service not found</h2>
        <Link to="/" className="back-link">Return to Home</Link>
      </div>
    );
  }

  return (
    <div className="service-detail">
      {/* Back Navigation */}
      <div className="service-nav">
        <Link to="/" className="back-btn">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
          </svg>
          Back to Home
        </Link>
      </div>

      {/* Hero Section */}
      <section className="service-hero">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="service-badge">
            <span>Specialized Treatment</span>
          </div>
          <h1 className="hero-title">{service.title}</h1>
          <h2 className="hero-subtitle">{service.subtitle}</h2>
          <p className="hero-description">{service.description}</p>
          <div className="hero-actions">
            <Link to="/#contact" className="cta-primary">Book Consultation</Link>
            <a href="tel:+1234567890" className="cta-secondary">Call Now</a>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="service-overview">
        <div className="container">
          <div className="overview-grid">
            <div className="overview-text">
              <h3>Overview</h3>
              <p>{service.mainContent.overview}</p>
            </div>
            <div className="benefits-list">
              <h3>Benefits</h3>
              <ul>
                {service.mainContent.benefits.map((benefit, index) => (
                  <li key={index}>
                    <svg viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Treatment Process */}
      <section className="treatment-process">
        <div className="container">
          <h3 className="section-title">Treatment Process</h3>
          <div className="process-steps">
            {service.mainContent.process.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h4>{step.title}</h4>
                  <p>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment Options */}
      <section className="treatment-options">
        <div className="container">
          <h3 className="section-title">Treatment Options</h3>
          <div className="treatments-grid">
            {service.treatments.map((treatment, index) => (
              <div key={index} className="treatment-detail-card">
                <div className="treatment-info">
                  <h4>{treatment.name}</h4>
                  <p className="treatment-description">{treatment.description}</p>
                  
                  <div className="treatment-meta">
                    <div className="meta-item">
                      <span className="meta-label">Duration:</span>
                      <span className="meta-value">{treatment.duration}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Price:</span>
                      <span className="meta-value">{treatment.price}</span>
                    </div>
                  </div>

                  <div className="treatment-features">
                    <h5>Key Features:</h5>
                    <ul>
                      {treatment.features.map((feature, idx) => (
                        <li key={idx}>{feature}</li>
                      ))}
                    </ul>
                  </div>

                  <button className="learn-more-btn">Learn More</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="service-faqs">
        <div className="container">
          <h3 className="section-title">Frequently Asked Questions</h3>
          <div className="faqs-list">
            {service.faqs.map((faq, index) => (
              <div key={index} className="faq-item">
                <h4 className="faq-question">{faq.question}</h4>
                <p className="faq-answer">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="service-cta">
        <div className="container">
          <div className="cta-content">
            <h3>Ready to Get Started?</h3>
            <p>Schedule your consultation today and take the first step towards your perfect smile.</p>
            <div className="cta-actions">
              <Link to="/appointment" className="cta-primary">Book Consultation</Link>
              <a href="tel:+923080006494" className="cta-secondary">Call 0308 0006494</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail; 